import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Button,
  Spin,
  Space,
  Select,
  Row,
  Col,
  DatePicker,
  Table,
  Pagination,
  Switch,
  Form,
  message,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery, gql, useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import formatAmount from 'indian-currency-formatter';

import {
  updateGridOptions,
  updateGridMetaData,
  updateGridFilters,
} from '../../store/reducers/reportSlice';
import { REPORT_INCENTIVES_SUMMARY_FIELDS } from '../../apollo/fragments';
import { CATEGORIES_GET_QUERY } from '../../apollo/queries';
import DataGrid from '../../components/DataGrid';
import * as Config from '../../appConfig';

var fileDownload = require('js-file-download');

const REPORT_INCENTIVES_SUMMARY_REPORT_QUERY = gql`
  ${REPORT_INCENTIVES_SUMMARY_FIELDS}
  query IncentivesReport(
    $first: Int
    $page: Int
    $options: [QueryGetIncentivesReportOptions]
    $filters: [QueryGetIncentivesReportFilter]
  ) {
    incentivesReport(
      first: $first
      page: $page
      filters: $filters
      options: $options
    ) {
      paginatorInfo {
        count
        currentPage
        total
      }
      data {
        ...IncnetivesSummaryReportFields
      }
    }
  }
`;

const { Option } = Select;
const { RangePicker } = DatePicker;

const IncentivesSummaryReport = ({ history, match, gridId }) => {
  const { metaData, grids } = useSelector((state) => state.report);
  const {
    user: { ability },
  } = useSelector((state) => state.login);
  const dispatch = useDispatch();
  const [columns, setColumns] = useState([]);
  const [exportInProgress, setExportInProgress] = useState(false);

  const grid = grids.find((i) => i.id === gridId);

  const isGroupByAM = grid.metaData.options.find(
    (i) => i.option === 'GROUPBY_AM'
  ).value;

  const isGroupByPortfolio = grid.metaData.options.find(
    (i) => i.option === 'GROUPBY_PORTFOLIO'
  ).value;

  const locationFilter = grid.metaData.filters.find(
    (i) => i.column === 'LOCATION'
  )
    ? grid.metaData.filters.find((i) => i.column === 'LOCATION').value
    : [];
  const dateFilter = grid.metaData.filters.find((i) => i.column === 'FOR_MONTH')
    ? grid.metaData.filters.find((i) => i.column === 'FOR_MONTH').value
    : [];

  const queryVariables = useMemo(() => {
    return {
      first: grid.metaData.pagination.pageSize,
      page: grid.metaData.pagination.page,
      filters: grid.metaData.filters,
      options: grid.metaData.options,
    };
  }, [
    grid.metaData.pagination.pageSize,
    grid.metaData.pagination.page,
    grid.metaData.filters,
    grid.metaData.options,
  ]);

  const { loading: loadingCategory, data: catData } = useQuery(
    CATEGORIES_GET_QUERY,
    {
      variables: {
        type: 'PRODUCT_TYPE',
      },
    }
  );

  const { loading, data, error, refetch } = useQuery(
    REPORT_INCENTIVES_SUMMARY_REPORT_QUERY,
    {
      variables: queryVariables,
    }
  );

  useEffect(() => {
    if (grid) {
      console.log('refetch for metaData with///', queryVariables);
      refetch(queryVariables);
    }
  }, [dispatch, refetch, grid, queryVariables]);

  useEffect(() => {
    if (catData && catData.categories) {
      console.log('categories fetched ///', catData.categories);
      const catgoriesWithTotal = [...catData.categories, { code: 'TOTAL' }];
      let columns2 = [
        {
          title: 'Location',
          dataIndex: 'location_name',
          key: 'location_name',
          serverKey: 'LOCATION',
          dataType: 'string',
          className: 'width-125',
        },
        {
          title: 'Month',
          dataIndex: 'for_month',
          key: 'for_month',
          serverKey: 'FOR_MONTH',
          dataType: 'date',
          format: 'month',
          className: 'width-100',
          render: (for_month) =>
            moment(for_month).format(Config.DATE_MONTH_FORMAT),
        },
        {
          title: 'AM',
          dataIndex: 'am_emp_code',
          key: 'am_emp_code',
          serverKey: 'AM',
          dataType: 'string',
          className: 'width-125',
          hidden: !isGroupByAM,
        },
        {
          title: 'Portfolio',
          dataIndex: 'portfolio_name',
          key: 'portfolio_name',
          serverKey: 'PORTFOLIO',
          dataType: 'string',
          className: 'width-125',
          hidden: !isGroupByPortfolio,
        },
        {
          title: 'FE INCENTIVES',
          dataIndex: 'fe_incentive',
          key: 'fe_incentive',
          dataType: 'cell-currency',
          children: catgoriesWithTotal.map((productType) => {
            return {
              title: productType.code,
              dataIndex: 'fe_incentive',
              key: `fe_incentive_${productType.code}`,
              serverKey: `FE_${productType.code}`,
              dataType: 'cell-currency',
              className: 'cell-currency',
              render: (fe_incentive) => {
                return formatAmount(
                  fe_incentive.find((i) => i.name === productType.code).value
                );
              },
            };
          }),
        },
        {
          title: 'TL INCENTIVES',
          dataIndex: 'tl_incentive',
          key: 'tl_incentive',
          dataType: 'cell-currency',
          children: catgoriesWithTotal.map((productType) => {
            return {
              title: productType.code,
              dataIndex: 'tl_incentive',
              key: `tl_incentive_${productType.code}`,
              dataType: 'cell-currency',
              serverKey: `TL_${productType.code}`,
              className: 'cell-currency',
              render: (tl_incentive) => {
                return formatAmount(
                  tl_incentive.find((i) => i.name === productType.code).value
                );
              },
            };
          }),
        },
      ].filter((i) => !i.hidden);
      setColumns(columns2);
    }
  }, [catData, isGroupByAM, isGroupByPortfolio]);

  const onToggleOptions = (checked, optionName) => {
    console.log('onToggleOptions///', checked, optionName);
    dispatch(
      updateGridOptions({
        gridId,
        option: {
          option: optionName,
          value: checked,
        },
      })
    );
  };

  const onChange = (updatedMetaData) => {
    console.log('onChange///', updatedMetaData);
    dispatch(
      updateGridMetaData({
        gridId,
        metaData: updatedMetaData,
      })
    );
  };

  const handleApplyFilter = (values) => {
    console.log('handleApplyFilter ///', values);
    dispatch(
      updateGridFilters({
        gridId,
        filters: [
          {
            column: 'LOCATION',
            value: values.location,
          },
          {
            column: 'FOR_MONTH',
            value:
              values.monthRange && values.monthRange && values.monthRange[1]
                ? [
                    values.monthRange[0].format(Config.DATE_FORMAT_SERVER),
                    values.monthRange[1].format(Config.DATE_FORMAT_SERVER),
                  ]
                : null,
          },
        ],
      })
    );
  };

  const onExport = (onFinish) => {
    setExportInProgress(true);
    console.log('onExport Clicked');
    const columnsToExport = [];
    columns.forEach((col) => {
      if (col.children) {
        col.children.forEach((innerCol) => {
          columnsToExport.push({
            column: innerCol.serverKey,
            dataType: innerCol.dataType,
            heading: innerCol.serverKey,
          });
        });
      } else {
        columnsToExport.push({
          column: col.dataIndex,
          dataType: col.dataType,
          heading: col.serverKey,
        });
      }
    });

    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/report/incentives-summary-export`,
      {
        method: 'POST',
        cache: 'no-cache',
        headers: new Headers({
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        }),
        body: JSON.stringify({
          filters: grid.metaData.filters,
          options: grid.metaData.options,
          columns: columnsToExport,
        }),
      }
    )
      .then((response) => {
        if (response.status === 200) {
          return response.blob();
        } else {
          onFinish('error');
          throw new Error('Error');
        }
      })
      .then((blob) => {
        fileDownload(
          blob,
          `${gridId}-${new Date()
            .toLocaleString()
            .replaceAll('/', '-')
            .replace(',', '')
            .replaceAll(' ', '_')}.xlsx`
        );
        setExportInProgress(false);
      })
      .catch((e) => {
        setExportInProgress(false);
        message.error('Something went wrong, Please try again!');
      });
  };

  return (
    <Card className="expenseCard" style={{ minHeight: '200px' }}>
      <Spin spinning={loading || loadingCategory}>
        <Row>
          <Col xs={24} md={22} style={{ marginBottom: '1rem' }}>
            <Space>
              <Form
                name="basic"
                initialValues={{
                  location: locationFilter,
                  monthRange:
                    dateFilter && dateFilter.map((date) => moment(date)),
                }}
                onFinish={handleApplyFilter}
                autoComplete="off"
                layout="inline"
              >
                <Form.Item name="location">
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ minWidth: 150, maxWidth: 300 }}
                    placeholder="Any Location"
                  >
                    <Option value={1}>NGL</Option>
                    <Option value={2}>MAR</Option>
                    <Option value={3}>MDU</Option>
                    <Option value={4}>TRI</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="monthRange">
                  <RangePicker picker="month" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    Apply Filter
                  </Button>
                </Form.Item>
              </Form>
              <div>
                <Switch
                  size="small"
                  defaultChecked={isGroupByAM}
                  onChange={(checked, event) => {
                    onToggleOptions(checked, 'GROUPBY_AM');
                  }}
                />{' '}
                Agency Manager
              </div>
              <div>
                <Switch
                  size="small"
                  defaultChecked={isGroupByPortfolio}
                  onChange={(checked, event) => {
                    onToggleOptions(checked, 'GROUPBY_PORTFOLIO');
                  }}
                />{' '}
                Portfolio
              </div>
            </Space>
          </Col>
          <Col
            xs={24}
            md={2}
            style={{ marginBottom: '1rem', textAlign: 'right' }}
          >
            <Space>
              <Button
                type="secondary"
                onClick={onExport}
                icon={<DownloadOutlined />}
                loading={exportInProgress}
                disabled={exportInProgress}
              >
                Export
              </Button>
              {/* <div>
                <Switch size="small" defaultChecked /> FE Incentives
              </div>
              <div>
                <Switch size="small" defaultChecked /> TL Incentives
              </div>
              <div>
                <Switch size="small" defaultChecked /> Agency Manager
              </div>
              <div>
                <Switch size="small" defaultChecked /> Portfolio
              </div> */}
            </Space>
          </Col>
        </Row>
        {columns.length && data && (
          <div className="dataGrid">
            <Table
              size="small"
              bordered={true}
              columns={columns}
              dataSource={data.incentivesReport.data}
              scroll={{ x: '1500' }}
              pagination={false}
            />
            {data.incentivesReport.data.length > 0 && (
              <Pagination
                // size="small"
                {...grid.metaData.pagination}
                total={data.incentivesReport.paginatorInfo.total}
                onChange={(page, pageSize) => {
                  onChange({
                    pagination: {
                      ...grid.metaData.pagination,
                      page: page,
                      current: page,
                      pageSize: pageSize,
                    },
                  });
                }}
                showTotal={(total, range) =>
                  `Showing ${range[0]}-${range[1]} of ${total} records`
                }
              />
            )}
          </div>
        )}
      </Spin>
    </Card>
  );
};

export default IncentivesSummaryReport;
